import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import StylePackItem from './style-pack-item';
import StylePackRequestBtn from './style-pack-request-btn';
import { StylePackContentType, StylePackData, StylePackGroup, StylePackItemTypes, templateLabels, templateSubLabels } from '../types/stylepacks';
import ButtonClose from '../general-components/button-close';
import { stylePackBannerImages } from '../../utils/content-templates';
import styled from 'styled-components';
import StylePackColorPalettes from './style-pack-color-palettes';
import ButtonBack from '../general-components/button-back';
import { mediaInfoSkele, titleInfoSkele } from './skeleton-sizes';

const BannerContainer = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	width: 100%;
	z-index: -1;
`;

const BannerImage = styled.img`
	width: 100%;
`;

const BannerGradient = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: linear-gradient(180deg, rgba(38, 36, 43, 0.45) -60.83%, rgba(38, 36, 43, 0.69) 38.59%, rgba(38, 36, 43, 0.93) 67.15%, #26242B 98.03%);
`;

export interface StylePacksInfoProps {
	stylePack?: StylePackData;
	selectedItem: string;
	goBack: () => void;
	closeStylePackTab: () => void;
	doRender?: boolean;
}

export interface InfoMap {
	[key: string]: string;
}

const backupStylePack: StylePackData = {
	id: 'backup',
	name: 'Miscellaneous',
	locked: false,
	stylePack: [],
};

const displayTitlesFirst = (a:StylePackGroup, b:StylePackGroup) => {
	if (a.type === 'title') return -1;
	else if (b.type === 'title') return 1;
	else return 0;
}

export default function StylePackInfo(props: StylePacksInfoProps) {	
	const {
		stylePack,
		selectedItem = '',
		goBack,
		closeStylePackTab
	} = props;

	const name = stylePack ? stylePack.name : backupStylePack.name;
	const locked = stylePack ? stylePack.locked : backupStylePack.locked;
	const stylePackContent = stylePack ? stylePack.stylePack : backupStylePack.stylePack;
	const id = stylePack ? stylePack.id : backupStylePack.id;

	useEffect(() => {
		if (selectedItem) {
			setTimeout(() => {
				const item = document.getElementById(`style-pack-info-content-item-${selectedItem}`);
				if (item) {
					item.scrollIntoView({behavior: 'smooth', block: 'end', inline: 'nearest'});
				}
			}, 500);
		}
	}, [selectedItem]);

	const isMiscellaneous = useMemo(() => id === 'miscellaneous', [id]);

	const bannerImage = stylePackBannerImages[id];

	function handleStylepackClose () {
		goBack();
		closeStylePackTab();
	}

	if (!stylePack?.id) {
		return null;
	}

	return (<div
		className={classNames('style-pack-info', { 
			'has-request-button': locked,
		})}
		data-testid="style-pack-info"
	>
		<div className="style-pack-info-content" data-testid="style-pack-info-content">
			{bannerImage && (
				<BannerContainer className='banner-container'>
					<BannerImage src={bannerImage} />
					<BannerGradient />
				</BannerContainer>
			)}
			<div className='style-pack-info-content-header'>
				<ButtonBack onClick={goBack}></ButtonBack>
				<h3 className='bl-font-header'>{name}</h3>
				<ButtonClose onClick={handleStylepackClose} noPadding moreClassNames="margin-left-auto"/>
			</div>
			<StylePackColorPalettes stylepack={name}/>
			{stylePackContent.sort(displayTitlesFirst).map((pack, index) => (
				<div key={`style-pack-info-${index}`}>
					{pack.templates.length > 0 && (
						<>
							<div
								className="style-pack-info-content-title"
								data-testid={`style-pack-info-content-title-${index}`}
								key={`style-pack-info-content-title-${index}`}
							>
								<span>{templateLabels[pack.type]}</span>
								{templateSubLabels[pack.type] !== '' && (<span className='style-pack-info-content-subtitle'>{templateSubLabels[pack.type]}</span>)}
							</div>
							<div
								className={classNames('style-pack-info-content-items', { misc: isMiscellaneous, title: pack.type === 'title' })}
								data-testid={`style-pack-info-content-items-${index}`}
								key={`style-pack-info-content-items-${index}`}
							>
								{pack.templates.map((item, _index) => (
									<React.Fragment key={`style-pack-info-item-wrapper-${item.id}-${_index}`}>
										<StylePackItem
											key={`style-pack-info-item-${item.id}-${_index}`}
											item={item}
											type={StylePackItemTypes.Info}
											skeletonSize={item.contentType === StylePackContentType.TITLE 
												? {...titleInfoSkele, style: {top: '-3px'}} 
												: mediaInfoSkele}
										/>
									</React.Fragment>
								))}
							</div>
						</>
					)}
				</div>
			))}
			{!!locked && <StylePackRequestBtn stylePackName={name} existingMargin/>}
		</div>
	</div>);
}
